(() => {
  const SELECTOR = '[data-cart-recs]';
  const LIMIT = 3;

  // ---- Estado anti-parpadeo / anti-re-render ----
  let lastCartSignature = '';
  let isMounting = false;
  let lastMountAt = 0;

  // ---------------------------
  // Utils
  // ---------------------------
  const debounce = (fn, wait = 250) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };

  const fetchJSON = async (url, options = {}) => {
    const res = await fetch(url, { credentials: 'same-origin', ...options });
    if (!res.ok) throw new Error(`HTTP ${res.status} for ${url}`);
    return res.json();
  };

  const fetchText = async (url, options = {}) => {
    const res = await fetch(url, { credentials: 'same-origin', ...options });
    if (!res.ok) throw new Error(`HTTP ${res.status} for ${url}`);
    return res.text();
  };

  const money = (cents, currency = 'EUR') => {
    const locale = document.documentElement.lang || 'es';
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency }).format((cents || 0) / 100);
    } catch {
      return `${((cents || 0) / 100).toFixed(2)} ${currency}`;
    }
  };

  const withWidth = (src, w = 260) => {
    if (!src) return '';
    if (src.startsWith('//')) src = 'https:' + src;
    if (!src.includes('width=')) src += (src.includes('?') ? '&' : '?') + `width=${w}`;
    return src;
  };

  const getImgSrc = (p) => {
    let src = '';
    if (typeof p.featured_image === 'string') src = p.featured_image;
    else if (p.featured_image && typeof p.featured_image === 'object') {
      src = p.featured_image.url || p.featured_image.src || '';
    }
    if (!src && Array.isArray(p.images) && p.images.length) {
      const f = p.images[0];
      src = typeof f === 'string' ? f : (f.url || f.src || '');
    }
    return withWidth(src, 260);
  };

  const pickVariant = (p) => {
    const v = p.variants || [];
    return v.find((x) => x.available) || v[0] || null;
  };

  // ---------------------------
  // Shopify endpoints
  // ---------------------------
  const getCart = () => fetchJSON('/cart.js');

  const getRecommendations = (productId, limit = 12) =>
    fetchJSON(`/recommendations/products.json?product_id=${productId}&limit=${limit}&intent=related`);

  const addToCart = async (variantId) => {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
    });
    if (!res.ok) throw new Error(await res.text());
  };

  // ---------------------------
  // ✅ REFRESH REAL Maker drawer (cart-element.cart--root)
  // ---------------------------
  const refreshMakerDrawer = async () => {
    const html = await fetchText('/cart?view=drawer');
    const doc = new DOMParser().parseFromString(html, 'text/html');

    const freshRoot =
      doc.querySelector('cart-element.cart--root[data-view="drawer"]') ||
      doc.querySelector('cart-element.cart--root') ||
      doc.querySelector('.cart--wrapper');

    if (!freshRoot) {
      console.warn('[cart-recs] /cart?view=drawer no contiene cart--root/cart--wrapper');
      return false;
    }

    const drawerContainer =
      document.querySelector('.cartDrawer') ||
      document.querySelector('[data-cart-drawer]') ||
      document.querySelector('#CartDrawer') ||
      document.body;

    const currentRoot =
      drawerContainer.querySelector('cart-element.cart--root[data-view="drawer"]') ||
      drawerContainer.querySelector('cart-element.cart--root');

    if (currentRoot && freshRoot.matches('cart-element.cart--root, cart-element.cart--root[data-view="drawer"]')) {
      currentRoot.innerHTML = freshRoot.innerHTML;
      return true;
    }

    const currentWrap = drawerContainer.querySelector('.cart--wrapper');
    if (currentWrap && freshRoot.matches('.cart--wrapper')) {
      currentWrap.innerHTML = freshRoot.innerHTML;
      return true;
    }

    if (currentWrap && freshRoot.querySelector('.cart--wrapper')) {
      currentWrap.innerHTML = freshRoot.querySelector('.cart--wrapper').innerHTML;
      return true;
    }

    console.warn('[cart-recs] No se encontró en el DOM actual cart--root/cart--wrapper para reemplazar.');
    return false;
  };

  // ---------------------------
  // Render (anti-parpadeo: no reasignar src si es el mismo)
  // ---------------------------
  const render = (container, products, currency) => {
    const itemsWrap = container.querySelector('[data-cart-recs-items]');
    const tpl = container.querySelector('[data-cart-recs-template]');
    if (!itemsWrap || !tpl) return;

    // ✅ Guard: si ya está renderizado el mismo set de productos, no tocar DOM
    const newIds = products.map(p => String(p.id)).join(',');
    const oldIds = itemsWrap.getAttribute('data-render-ids') || '';
    if (oldIds === newIds && itemsWrap.children.length) return;
    itemsWrap.setAttribute('data-render-ids', newIds);

    itemsWrap.innerHTML = '';

    products.forEach((p) => {
      const variant = pickVariant(p);
      if (!variant) return;

      const node = tpl.content.firstElementChild.cloneNode(true);

      const urlEl = node.querySelector('[data-rec-url]');
      const imgEl = node.querySelector('[data-rec-image]');
      const titleEl = node.querySelector('[data-rec-title]');
      const priceEl = node.querySelector('[data-rec-price]');
      const addBtn = node.querySelector('[data-rec-add]');

      const productUrl = p.url || (p.handle ? `/products/${p.handle}` : '#');

      if (urlEl) urlEl.href = productUrl;
      if (titleEl) {
        titleEl.href = productUrl;
        titleEl.textContent = p.title || 'Producto';
      }

      if (imgEl) {
        const src = getImgSrc(p) || '';
        // ✅ Anti-flicker: set src una sola vez
        if (imgEl.getAttribute('src') !== src) imgEl.src = src;
        imgEl.alt = p.title || '';
        imgEl.decoding = 'async';
        imgEl.loading = 'eager'; // drawer: mejor eager para evitar flicker móvil
        imgEl.width = 112;
        imgEl.height = 112;
      }

      const cents = (variant.price != null ? variant.price : p.price) || 0;
      if (priceEl) priceEl.textContent = money(cents, currency);

      if (addBtn) {
        addBtn.setAttribute('data-variant-id', String(variant.id));
        addBtn.type = 'button';
      }

      itemsWrap.appendChild(node);
    });
  };

  const buildRecommendations = async (container) => {
    try {
      const cart = await getCart();
      const currency = cart.currency || window.Shopify?.currency?.active || 'EUR';

      if (!cart?.items?.length) {
        container.style.display = 'none';
        return;
      }

      const cartProductIds = new Set(cart.items.map((i) => i.product_id));
      const items = cart.items.slice().reverse();
      const collected = [];

      for (const item of items) {
        if (collected.length >= LIMIT) break;

        const rec = await getRecommendations(item.product_id, 12);

        (rec.products || [])
          .filter((p) => !cartProductIds.has(p.id))
          .filter((p) => (p.variants || []).some((v) => v.available))
          .forEach((p) => {
            if (collected.length < LIMIT && !collected.some((x) => x.id === p.id)) collected.push(p);
          });
      }

      if (!collected.length) {
        container.style.display = 'none';
        return;
      }

      container.style.display = '';
      render(container, collected.slice(0, LIMIT), currency);
    } catch (e) {
      console.warn('[cart-recs] build error', e);
      container.style.display = 'none';
    }
  };

  // ---------------------------
  // ✅ Mount: solo si cambia el carrito
  // ---------------------------
  const mountAll = debounce(async () => {
    // evita ráfagas
    const now = Date.now();
    if (now - lastMountAt < 200) return;
    lastMountAt = now;

    if (isMounting) return;
    isMounting = true;

    try {
      const cart = await getCart();
      const signature = (cart.items || []).map(i => `${i.key}:${i.quantity}`).join('|');

      // ✅ Si el carrito no cambió, NO re-render -> adiós vibración
      if (signature && signature === lastCartSignature) return;
      lastCartSignature = signature;

      document.querySelectorAll(SELECTOR).forEach(buildRecommendations);
    } catch (e) {
      // fallback: intenta igual
      document.querySelectorAll(SELECTOR).forEach(buildRecommendations);
    } finally {
      isMounting = false;
    }
  }, 250);

  // ---------------------------
  // Click Add (capture)
  // ---------------------------
  const onClick = async (e) => {
    const btn = e.target?.closest?.('[data-rec-add]');
    if (!btn) return;

    e.preventDefault();
    e.stopPropagation();
    if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();

    const variantId = btn.getAttribute('data-variant-id');
    if (!variantId) return;

    try {
      // ⚠️ si no quieres spinner, usa pointerEvents en vez de disabled
      btn.style.pointerEvents = 'none';

      await addToCart(variantId);
      await refreshMakerDrawer();

      // fuerza firma nueva + rebuild
      lastCartSignature = '';
      mountAll();
    } catch (err) {
      console.warn('[cart-recs] add error', err);
    } finally {
      btn.style.pointerEvents = 'auto';
    }
  };

  // ---------------------------
  // Init: observer “suave”
  // - NO observa todo y re-renderiza sin parar
  // - Solo monta cuando aparece el drawer en DOM
  // ---------------------------
  const initSoftObserver = () => {
    if (!('MutationObserver' in window)) return;

    let cooldown = false;
    const obs = new MutationObserver(() => {
      if (cooldown) return;

      // si el drawer está presente, montamos UNA vez y entramos en cooldown
      if (document.querySelector('cart-element.cart--root') || document.querySelector('.cartDrawer')) {
        cooldown = true;
        mountAll();
        setTimeout(() => { cooldown = false; }, 800);
      }
    });

    obs.observe(document.body, { childList: true, subtree: true });
  };

  const init = () => {
    mountAll();
    document.addEventListener('click', onClick, true);
    initSoftObserver();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();